import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import {
  LogIn,
  QrCode,
  Loader2,
  Camera,
  CameraOff,
  KeyRound,
  UserPlus,
} from 'lucide-react';
import Footer from '../components/Footer';
import { saveEcoparqueSession } from '../utils/ecoparqueSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCANNER_ID = 'eco-login-scanner';

export default function EcoparqueLogin() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const stoppingRef = useRef(false);

  useEffect(() => {
    return () => {
      stopScanner();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    };
  }, []);

  const stopScanner = async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    try {
      if (scannerRef.current) {
        try {
          await scannerRef.current.stop();
        } catch (_) {}
        try {
          scannerRef.current.clear();
        } catch (_) {}
        scannerRef.current = null;
      }
    } finally {
      stoppingRef.current = false;
      setScanning(false);
    }
  };

  const handleLogin = async ({ user_code, qr_data }) => {
    if (loading) return;
    setLoading(true);
    try {
      const body = {};
      if (user_code) body.user_code = user_code;
      if (qr_data) body.qr_data = qr_data;
      const res = await fetch(`${BACKEND_URL}/api/ecoparque/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'No se pudo iniciar sesión');
        return;
      }
      saveEcoparqueSession(data);
      toast.success(`Hola de nuevo, ${data.nombre}`);
      await stopScanner();
      navigate('/ecoparque');
    } catch (err) {
      console.error(err);
      toast.error('Error de red. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const clean = code.trim().toUpperCase();
    if (!clean) {
      toast.error('Introduce tu código');
      return;
    }
    handleLogin({ user_code: clean });
  };

  const startScanner = async () => {
    if (scanning) return;
    try {
      const instance = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = instance;
      setScanning(true);
      await instance.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          // Al primer escaneo válido, paramos y hacemos login
          await stopScanner();
          handleLogin({ qr_data: decodedText });
        },
        () => {
          /* ignore per-frame errors */
        }
      );
    } catch (err) {
      console.error('No se pudo iniciar la cámara', err);
      toast.error('No se pudo acceder a la cámara. Comprueba permisos o usa el código manual.');
      await stopScanner();
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFCFE]">
      <Helmet>
        <title>Acceso Mi Ecoparque | Cuidemos Los Alcázares®</title>
      </Helmet>

      <section className="pt-24 pb-12 px-6" style={{ background: 'linear-gradient(135deg, #1F8A70 0%, #1E5AA8 100%)' }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <LogIn size={26} className="text-white" />
            </div>
            <span className="text-white/90 text-sm font-medium uppercase tracking-wider">Ciudadanía</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white mb-3">Acceso Mi Ecoparque</h1>
          <p className="text-white/80 max-w-xl">
            Escanea tu QR o introduce tu código de usuario para acceder a tu portal.
          </p>
        </div>
      </section>

      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Escáner QR */}
          <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <QrCode size={22} className="text-[#1E5AA8]" />
              <h2 className="font-display text-2xl text-[#0A2540]">Escanear mi QR</h2>
            </div>
            <p className="text-sm text-[#6F8AA8] mb-4">
              Permite el acceso a la cámara y enfoca tu código QR.
            </p>
            <div
              id={SCANNER_ID}
              className="rounded-2xl overflow-hidden border border-[#CFE3F5] bg-[#F5F9FC] min-h-[260px] flex items-center justify-center text-[#6F8AA8] text-sm"
              data-testid="eco-login-scanner"
            >
              {!scanning && <span>Cámara detenida</span>}
            </div>
            <div className="mt-4 flex gap-3">
              {!scanning ? (
                <button
                  type="button"
                  onClick={startScanner}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F8A70] text-white text-sm font-medium hover:bg-[#177A60] transition-colors disabled:opacity-60"
                  data-testid="eco-login-start-scan-btn"
                >
                  <Camera size={16} />
                  Iniciar escáner
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopScanner}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#a8350a] transition-colors"
                  data-testid="eco-login-stop-scan-btn"
                >
                  <CameraOff size={16} />
                  Detener
                </button>
              )}
            </div>
          </div>

          {/* Código manual */}
          <form
            onSubmit={handleManualSubmit}
            className="rounded-3xl border border-[#CFE3F5] bg-white p-6 sm:p-8 shadow-sm flex flex-col"
            data-testid="eco-login-manual-form"
          >
            <div className="flex items-center gap-2 mb-4">
              <KeyRound size={22} className="text-[#1E5AA8]" />
              <h2 className="font-display text-2xl text-[#0A2540]">Con mi código</h2>
            </div>
            <p className="text-sm text-[#6F8AA8] mb-4">
              Si tienes tu código a mano (ej. <code className="font-mono text-[#0A2540]">ECO-ABCDE</code>), introdúcelo aquí.
            </p>
            <label className="block">
              <span className="text-xs font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">Código de usuario</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540] font-mono uppercase tracking-widest"
                placeholder="ECO-XXXXX"
                data-testid="eco-login-code-input"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] transition-colors disabled:opacity-60"
              data-testid="eco-login-submit-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              {loading ? 'Accediendo…' : 'Acceder'}
            </button>
            <div className="mt-6 pt-6 border-t border-[#CFE3F5]">
              <p className="text-xs text-[#6F8AA8] mb-2">¿Aún no te has registrado?</p>
              <Link
                to="/ecoparque/registro"
                className="inline-flex items-center gap-2 text-[#1E5AA8] text-sm font-medium hover:text-[#0F4C97]"
                data-testid="eco-login-go-registro-link"
              >
                <UserPlus size={16} />
                Crear una cuenta
              </Link>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
